import { useCallback, useRef, useState, MutableRefObject, Dispatch, SetStateAction } from 'react';
import firebase from 'firebase/compat/app';
import { getValidSessionFromURL } from '../utils/lendAPIUtils';
import { buildResumeLink } from '../utils/buildResumeLink';
import { createBarebonesLoan } from '../utils/lendAPILoanHelper';
import { mapLendAPIToIterableCombinedAppPreOfferLogin } from '../utils/lendAPIIterableHelper';
import { sendCustomEventToIterable } from '../utils/iterable';
import { DEFAULT_SOURCE_ID } from '../constants';

export interface LendAPISessionPayload {
  session?: string;
  [key: string]: unknown;
}

interface UseLendAPILoginParams {
  auth: firebase.auth.Auth;
  firestore: firebase.firestore.Firestore;
  urlSessionId?: string;
  isResumeLinkRedirect?: () => boolean;
  onResumeLinkRedirect?: () => void;
  onLoginHandled?: () => void;
}

interface UseLendAPILoginResult {
  hasSession: boolean;
  setHasSession: Dispatch<SetStateAction<boolean>>;
  sessionIdRef: MutableRefObject<string>;
  storedEmailRef: MutableRefObject<string>;
  storedFirstNameRef: MutableRefObject<string>;
  storedLastNameRef: MutableRefObject<string>;
  storedRawCustomRef: MutableRefObject<string>;
  loanCreationInProgress: MutableRefObject<boolean>;
  handleLoginSuccess: () => Promise<void>;
  captureSessionFromPayload: (payload: LendAPISessionPayload | undefined) => void;
}

function getSourceFromUrl(): number {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    return source ? parseInt(source, 10) || DEFAULT_SOURCE_ID : DEFAULT_SOURCE_ID;
  } catch {
    return DEFAULT_SOURCE_ID;
  }
}

export default function useLendAPILogin({
  auth,
  firestore,
  urlSessionId,
  isResumeLinkRedirect,
  onResumeLinkRedirect,
  onLoginHandled,
}: UseLendAPILoginParams): UseLendAPILoginResult {
  const [hasSession, setHasSession] = useState<boolean>(() => !!getValidSessionFromURL());
  const sessionIdRef = useRef<string>('');
  const storedEmailRef = useRef<string>('');
  const storedFirstNameRef = useRef<string>('');
  const storedLastNameRef = useRef<string>('');
  const storedRawCustomRef = useRef<string>('');
  const loanCreationInProgress = useRef(false);
  const iterablePreOfferEventSent = useRef(false);

  const captureSessionFromPayload = useCallback((payload: LendAPISessionPayload | undefined) => {
    if (payload?.session && !sessionIdRef.current) {
      console.log('[useLendAPILogin] Session captured from event:', payload.session);
      sessionIdRef.current = payload.session;
      setHasSession(true);
    }
  }, []);

  const handleLoginSuccess = useCallback(async () => {
    if (loanCreationInProgress.current) {
      console.warn('[useLendAPILogin] Loan creation already in progress');
      return;
    }

    const currentSessionId = sessionIdRef.current;
    const { currentUser } = auth;

    if (!currentUser || currentUser.isAnonymous || !currentSessionId) {
      console.warn('[useLendAPILogin] Cannot create loan: missing user or session', {
        hasUser: !!currentUser,
        isAnonymous: currentUser?.isAnonymous,
        hasSession: !!currentSessionId,
      });
      onLoginHandled?.();
      return;
    }

    // Resume-link users who just authenticated via login modal: redirect instead of
    // creating a loan (the loan already exists for a resume-link session).
    if (urlSessionId && isResumeLinkRedirect?.()) {
      console.log('[useLendAPILogin] Resume-link user authenticated, redirecting to MyApps');
      onResumeLinkRedirect?.();
      return;
    }

    loanCreationInProgress.current = true;

    try {
      const resumeUrl = buildResumeLink(
        window.location.href,
        currentSessionId,
        storedEmailRef.current || undefined
      );
      const source = getSourceFromUrl();

      console.log('[useLendAPILogin] Creating bare bones loan', {
        userId: currentUser.uid,
        sessionId: currentSessionId,
        source,
        resumeLink: resumeUrl,
      });

      await createBarebonesLoan(firestore, {
        userId: currentUser.uid,
        sessionId: currentSessionId,
        source,
        resumeLink: resumeUrl,
        email: storedEmailRef.current || undefined,
        firstName: storedFirstNameRef.current || undefined,
        lastName: storedLastNameRef.current || undefined,
      });

      console.log('[useLendAPILogin] Bare bones loan created successfully');

      if (!iterablePreOfferEventSent.current) {
        iterablePreOfferEventSent.current = true;
        if (storedEmailRef.current) {
          try {
            const iterableEvent = mapLendAPIToIterableCombinedAppPreOfferLogin({
              email: storedEmailRef.current,
              firstName: storedFirstNameRef.current,
              lastName: storedLastNameRef.current,
              sessionId: currentSessionId,
              resumeLink: resumeUrl,
              customParams: storedRawCustomRef.current,
            });
            await sendCustomEventToIterable(iterableEvent);
            console.log(
              '[useLendAPILogin][Iterable] CombinedAppPreOfferLogin event sent successfully'
            );
          } catch (iterableError) {
            console.error(
              '[useLendAPILogin][Iterable] Error sending CombinedAppPreOfferLogin event:',
              iterableError
            );
          }
        } else {
          console.warn(
            '[useLendAPILogin][Iterable] Skipping CombinedAppPreOfferLogin - no email available'
          );
        }
      }
    } catch (error) {
      console.error('[useLendAPILogin] Error creating bare bones loan:', error);
    } finally {
      loanCreationInProgress.current = false;
    }

    onLoginHandled?.();
  }, [auth, firestore, urlSessionId, isResumeLinkRedirect, onResumeLinkRedirect, onLoginHandled]);

  return {
    hasSession,
    setHasSession,
    sessionIdRef,
    storedEmailRef,
    storedFirstNameRef,
    storedLastNameRef,
    storedRawCustomRef,
    loanCreationInProgress,
    handleLoginSuccess,
    captureSessionFromPayload,
  };
}
